import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useRevenueCat } from "@/hooks/useRevenueCat";
import { useEntitlements } from "./useEntitlements";

/**
 * "Restaurer mes achats" outside the paywall (audit M10 / App Store 3.1.1):
 * lets a subscriber who reinstalled or changed device recover the
 * entitlement without having to open the paywall first.
 *
 * Hidden when RevenueCat cannot run here (no SDK key for the platform, or no
 * session). The backend stays the source of truth: a successful restore only
 * triggers a refetch of `/me/entitlements`.
 */
export function RestorePurchasesButton({
  disabled = false,
}: {
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const { state, restore } = useRevenueCat();
  const { refetch } = useEntitlements();
  const [pending, setPending] = useState(false);

  if (state.status === "unconfigured") return null;

  const busy =
    pending ||
    state.status === "loading" ||
    state.status === "purchasing" ||
    state.status === "restoring" ||
    (state.status === "error" && state.source === "configure");

  async function handleRestore() {
    setPending(true);
    try {
      const outcome = await restore();
      if (outcome.status === "error") {
        toast.error(t("auth.account.restore_error"));
        return;
      }
      refetch();
      if (outcome.hasEntitlement) {
        toast.success(t("auth.account.restore_success"));
      } else {
        toast.info(t("auth.account.restore_none"));
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <Button
      variant="outline"
      className="w-full"
      onClick={() => void handleRestore()}
      disabled={disabled || busy}
    >
      {pending
        ? t("auth.account.restoring")
        : t("auth.account.restore_purchases")}
    </Button>
  );
}
